import { useEffect, useState } from 'react';

import { BooleanEnum } from 'enums';
import { sessionStorageUtils } from 'utils';

const PROBE_URL = 'https://nuc.vtbbo.ru/';
const SESSION_KEY = 'vtb-tls-probe';
const TIMEOUT_MS = 5000;

// Module-level singleton — one fetch per browser session regardless of how many components use this hook
let probePromise: Promise<boolean> | null = null;

function readCache(): boolean | null {
  const cached = sessionStorageUtils.get(SESSION_KEY);
  if (cached === null) {
    return null;
  }
  return cached === BooleanEnum.True;
}

function writeCache(value: boolean): void {
  sessionStorageUtils.set(
    SESSION_KEY,
    value ? BooleanEnum.True : BooleanEnum.False,
  );
}

async function runProbe(): Promise<boolean> {
  const cached = readCache();
  if (cached !== null) {
    return cached;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    await fetch(`${PROBE_URL}?_=${Date.now()}`, {
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    writeCache(true);
    return true;
  } catch {
    clearTimeout(timeoutId);
    writeCache(false);
    return false;
  }
}

export function useVtbTlsProbe(enabled = true): {
  isReachable: boolean | null;
  isLoading: boolean;
} {
  const [isReachable, setIsReachable] = useState<boolean | null>(() =>
    enabled ? readCache() : null,
  );

  useEffect(() => {
    if (!enabled || isReachable !== null) {
      return;
    }

    if (!probePromise) {
      probePromise = runProbe();
    }

    let cancelled = false;
    probePromise.then((result) => {
      if (!cancelled) {
        setIsReachable(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, isReachable]);

  return { isReachable, isLoading: enabled && isReachable === null };
}
